// src/onboarding/OnboardingTaskReveal.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: Step 4 — Task reveal. Renders the sunflower-bloom celebration screen
// with a completed plot plan + single plant pin. On Let's Tend the wizard
// closes, the view switches to Plants (UAT Gap 7), and the newly-added plant's
// card scrolls into view + pulses.

import { useState, useEffect, useRef } from 'react';
import OnbFrame from './OnbFrame.jsx';
import OnbPlotPlan from './OnbPlotPlan.jsx';
import { ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS } from './constants.js';
import { onbResolveSpotlightColors } from './spotlight.js';

export default function OnboardingTaskReveal({plants, setView, onComplete, step, total, areas}) {
  const [fadeIn, setFadeIn] = useState(false);
  // WR-04: snapshot the target plant id at mount so the click handler
  // doesn't read a stale `plants` closure if the parent re-renders.
  const targetIdRef = useRef(null);
  useEffect(() => {
    setFadeIn(true);
    const list = plants || [];
    targetIdRef.current = list.length > 0 ? list[list.length - 1].id : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = () => {
    const targetId = targetIdRef.current;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (targetId == null) {
      // D-10: no plant to pulse — land on Year view
      if (typeof setView === 'function') setView('year');
      onComplete();
      return;
    }
    // Phase 31.1 Plan 04 — UAT Gap 7 (2026-04-20): first-run users are more
    // likely to want to add more plants next than view the month schedule.
    // Route to Plants view and pulse the newly-added plant's card there.
    // Plants view cards already carry data-plant-id (see PlantDirectoryCard /
    // PlantCard render), so the scroll + pulse logic below resolves correctly
    // with only this one-line target change.
    if (typeof setView === 'function') setView('plants');
    onComplete();
    requestAnimationFrame(() => {
      try {
        const el = document.querySelector(`[data-plant-id="${targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
          if (!reduce) {
            el.classList.add('task-pulse');
            setTimeout(() => el.classList.remove('task-pulse'), 2000);
          }
        }
      } catch {}
    });
  };

  // Derive the just-added plant + plot plan data for D-03/D-14/D-15.
  const list = plants || [];
  const lastPlant = list.length > 0 ? list[list.length - 1] : null;
  // D-16 / Plan 06 Task 1 — thread `areas` through from OnboardingWizard so
  // the Reveal can resolve the plant's area name from the location id. Falls
  // back to `_onbAreaName` (set during a hypothetical direct add) or the
  // default "Back Garden" label (cosmetic only — Phase 31.1 UAT-Gap-7 tests
  // don't assert area text).
  const plantArea = (() => {
    if (!lastPlant) return "Back Garden";
    const loc = (lastPlant.locations && lastPlant.locations[0]) || null;
    if (loc && Array.isArray(areas)) {
      const match = areas.find(a => a.id === loc.areaId);
      if (match) return match.name;
    }
    if (lastPlant._onbAreaName) return lastPlant._onbAreaName;
    return "Back Garden";
  })();
  const plotAreas = (() => {
    const geom = ONB_AREA_PATHS[plantArea] || ONB_AREA_PATH_FALLBACK;
    return [{
      name: plantArea, label: plantArea,
      path: geom.path, lx: geom.lx, ly: geom.ly,
      color: geom.color, hatch: geom.hatch,
    }];
  })();
  const plantPin = (() => {
    const geom = ONB_AREA_PATHS[plantArea] || ONB_AREA_PATH_FALLBACK;
    if (!lastPlant) return [];
    const colors = onbResolveSpotlightColors(lastPlant.name || "");
    return [{ x: geom.lx, y: (geom.ly || 90) - 12, color: colors[0], label: lastPlant.name || "Your plant" }];
  })();
  const plantName = lastPlant?.name || "Your plant";

  return (
    <OnbFrame step={step ?? 5} total={total ?? 6} ground="noon">
      {/* NB: total=6 here matches the prototype's "6 of 6" on the final reveal,
          even though the current wizard runs step=5 as the final index.
          The rail shows 100% either way (pct = (5+1)/6 = 100%). */}

      {/* Sunflower bloom — centred at top, over the sky. Uses existing
          .onb-sun-petal CSS animation (D-14; already in src/onboarding.css).
          Decorative only; marked aria-hidden. */}
      <div style={{ position: "absolute", top: 72, left: 0, right: 0, display: "flex", justifyContent: "center", zIndex: 4 }} aria-hidden="true">
        <svg viewBox="0 0 180 240" width="150" height="200" style={{ maxWidth: "100%", height: "auto", opacity: fadeIn ? 1 : 0, transition: "opacity 600ms ease" }}>
          {/* ground line */}
          <line x1="20" y1="225" x2="160" y2="225" stroke="var(--c-border)" strokeWidth="1"/>
          {/* stem */}
          <path d="M 90 225 Q 88 180 92 140 Q 95 110 90 85" stroke="#5a7a4a" strokeWidth="3" fill="none" strokeLinecap="round"/>
          {/* leaves */}
          <path d="M 90 170 Q 65 155 55 170 Q 65 180 90 175 Z" fill="#6b8a55"/>
          <path d="M 92 150 Q 120 135 132 152 Q 120 162 92 156 Z" fill="#5a7a4a"/>
          {/* petals — drawn at their final positions; animation scales them
              from the disc centre (90, 85) outward, all at once */}
          <ellipse className="onb-sun-petal" cx="90"  cy="55"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="108" cy="60"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="120" cy="75"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="122" cy="95"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="115" cy="112" rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="100" cy="120" rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="80"  cy="120" rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="65"  cy="112" rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="58"  cy="95"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="60"  cy="75"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="72"  cy="60"  rx="8" ry="18" fill="#e8a547"/>
          <ellipse className="onb-sun-petal" cx="85"  cy="55"  rx="8" ry="18" fill="#e8a547"/>
          {/* disc — the anchor from which petals open; always visible */}
          <circle cx="90" cy="85" r="18" fill="#6b4a2b"/>
          <circle cx="90" cy="85" r="14" fill="#8b5f33"/>
        </svg>
      </div>

      <div className="onb-content" style={{ paddingTop: 10 }}>
        {/* spacer below the sunflower */}
        <div style={{ height: 212 }}/>
        <p className="onb-eyebrow" style={{ textAlign: "center" }}>Your garden's ready</p>
        <h1 className="onb-heading" style={{ textAlign: "center", margin: "0 auto 10px", maxWidth: "100%" }}>
          <em>Potter your way</em>
        </h1>
        <p className="onb-sub" style={{ textAlign: "center", margin: "0 auto" }}>
          {lastPlant
            ? <>{plantName} is planted in your <strong style={{ color: "var(--c-heading)" }}>{plantArea}</strong>. Here's what's due this month.</>
            : <>Your spaces are ready. Add your first plant from the Plants view.</>
          }
        </p>

        {/* D-03 — completed plot plan with single plant pin */}
        <div style={{ marginTop: 14 }}>
          <OnbPlotPlan areas={plotAreas} pins={plantPin} highlightArea={plantArea}/>
        </div>

        {/* D-15 — NO first-task card here. Just the CTA. */}
        {/* D-17 — NO Back/Skip. */}
        <div className="push-bottom">
          <button type="button" className="onb-cta" onClick={handleStart}>
            Let's Tend →
          </button>
        </div>
      </div>
    </OnbFrame>
  );
}
